import React, { useRef, useState } from 'react';
import { DialogProps } from '@radix-ui/react-dialog';
import { ListMagnifyingGlass } from '@phosphor-icons/react';

import { AnswerDiagnosticsSection } from './answer-diagnostics';
import { useConvosContext } from './convos';
import { DiscardForm, DiscardFormData, DiscardReason, SelectOpenHandle } from './discard-form';
import { DivButton } from '../ui/div-button';
import { ScrollArea } from '../ui/scroll-area';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

export { DiscardReason } from './discard-form';

interface FeedbackDialogProps extends DialogProps {
  id: string;
  discarded?: boolean;
  discardedMeta?: { reason: DiscardReason; suggestedAnswer?: string };
  className?: string;
}

export function FeedbackDialog({
  id,
  discarded,
  discardedMeta,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  ...args
}: FeedbackDialogProps): React.ReactNode {
  const { onDiscard, onApprove } = useConvosContext();
  const [internalOpen, setInternalOpen] = useState(false);
  const selectOpenRef = useRef<SelectOpenHandle>({ isOpen: false, close: () => {} });

  // Use external state if provided, otherwise use internal state
  const isOpen = externalOpen ?? internalOpen;
  const setIsOpen = externalOnOpenChange ?? setInternalOpen;

  const onSubmitForm = async (data: DiscardFormData) => {
    await onDiscard(id, data.reason, data.preferredAnswer).then(() => setIsOpen(false));
  };

  const handleApprove = async () => {
    await onApprove(id);
  };

  return (
    <Sheet modal={false} open={isOpen} onOpenChange={setIsOpen} {...args}>
      <SheetTrigger asChild onClick={(e) => e.stopPropagation()} onKeyUp={(e) => e.stopPropagation()}>
        <DivButton variant="shadowSurface" size="sm" aria-label="Inspect answer information">
          <ListMagnifyingGlass className="text-dark-600" /> Inspect
        </DivButton>
      </SheetTrigger>
      <SheetContent
        className="space-y-4 px-0"
        onEscapeKeyDown={(e) => {
          if (selectOpenRef.current.isOpen) {
            e.preventDefault();
            selectOpenRef.current.close();
          }
        }}
        onInteractOutside={(e) => {
          if (selectOpenRef.current.isOpen) {
            e.preventDefault();
          }
        }}
      >
        <SheetHeader className="px-4">
          <SheetTitle className="heading-sm m-0">Answer information</SheetTitle>
          <SheetDescription className="sr-only">
            Shows answer diagnostics and allows you to provide feedback.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-4rem)] pb-4">
          <DiscardForm
            discarded={discarded}
            discardedMeta={discardedMeta}
            onSubmit={onSubmitForm}
            onApprove={handleApprove}
            selectOpenRef={selectOpenRef}
          />
          <AnswerDiagnosticsSection messageId={id} />
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
